import type { ClerkUserRecord } from "@/types/ClerkUserRecord"

/** Minimal shape required for navigation item filtering. */
interface NavigableItem {
  base_href?: string
  subItems?: NavigableItem[]
  [key: string]: unknown
}

// Define restricted users and their allowed pages
const CONVERSATIONS_REVIEW_ACCESS = {
  allowedPages: ["/uploaded", "/review"],
  allowedBasePaths: ["/uploaded", "/review"],
}

const RESTRICTED_USERS = {
  "queenzy.felizar@gmail.com": CONVERSATIONS_REVIEW_ACCESS,
  "restricted-user@example.com": CONVERSATIONS_REVIEW_ACCESS,
}

// Define all available pages for reference
export const ALL_PAGES = [
  "/home",
  "/agents",
  "/personas",
  "/test-sets",
  "/metrics",
  "/templates",
  "/alerts",
  "/dashboard",
  "/simulated",
  "/scheduled",
  "/uploaded",
  "/review",
  "/settings",
]

/**
 * Check if a user has restricted access
 */
export function isRestrictedUser(userEmail: string): boolean {
  return userEmail in RESTRICTED_USERS
}

/**
 * Get allowed pages for a restricted user
 */
export function getAllowedPages(userEmail: string): string[] {
  const restrictedUser =
    RESTRICTED_USERS[userEmail as keyof typeof RESTRICTED_USERS]
  return restrictedUser?.allowedPages || ALL_PAGES
}

/**
 * Get allowed base paths for a restricted user
 */
export function getAllowedBasePaths(userEmail: string): string[] {
  const restrictedUser =
    RESTRICTED_USERS[userEmail as keyof typeof RESTRICTED_USERS]
  return restrictedUser?.allowedBasePaths || []
}

/**
 * Check if a user can access a specific page
 */
export function canAccessPage(userEmail: string, pagePath: string): boolean {
  if (!isRestrictedUser(userEmail)) {
    return true // Non-restricted users can access all pages
  }

  const allowedPages = getAllowedPages(userEmail)
  const allowedBasePaths = getAllowedBasePaths(userEmail)

  // Check exact page match
  if (allowedPages.includes(pagePath)) {
    return true
  }

  // Check if page starts with any allowed base path
  return allowedBasePaths.some((basePath) => pagePath.startsWith(basePath))
}

/**
 * Filter navigation items for restricted users
 */
export function filterNavigationItems<T extends NavigableItem>(
  navItems: T[],
  userEmail: string,
): T[] {
  if (!isRestrictedUser(userEmail)) {
    return navItems // Return all items for non-restricted users
  }

  const allowedBasePaths = getAllowedBasePaths(userEmail)

  return navItems.reduce<T[]>((result, item) => {
    const href = item.base_href
    const isMainItemAllowed =
      typeof href === "string" &&
      allowedBasePaths.some((basePath) => href.startsWith(basePath))

    // A section is a grouping label, so it stays as long as one of its
    // children is allowed — its own href only names where the group points.
    if (item.subItems && Array.isArray(item.subItems)) {
      const filteredSubItems = item.subItems.filter(
        (subItem: NavigableItem) => {
          const subHref = subItem.base_href
          if (!subHref || typeof subHref !== "string") {
            return false
          }
          return allowedBasePaths.some((basePath) =>
            subHref.startsWith(basePath),
          )
        },
      )

      if (filteredSubItems.length === 0) {
        return result
      }

      result.push({ ...item, subItems: filteredSubItems })
      return result
    }

    if (isMainItemAllowed) {
      result.push(item)
    }

    return result
  }, [])
}

/**
 * Get user access context from Clerk user record
 */
export function getUserAccessContext(user: ClerkUserRecord | null) {
  if (!user?.userEmail) {
    return {
      isRestricted: false,
      allowedPages: ALL_PAGES,
      allowedBasePaths: [],
    }
  }

  const isRestricted = isRestrictedUser(user.userEmail)

  return {
    isRestricted,
    allowedPages: isRestricted ? getAllowedPages(user.userEmail) : ALL_PAGES,
    allowedBasePaths: isRestricted ? getAllowedBasePaths(user.userEmail) : [],
  }
}
